import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  InputNumber,
  Typography,
  Space,
  Alert,
  message,
} from 'antd';
import { RobotOutlined, ThunderboltOutlined } from '@ant-design/icons';
import Layout from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const { Paragraph } = Typography;
const { TextArea } = Input;

interface AITestGenerateRequest {
  title: string;
  description?: string;
  block: 'products' | 'vehicles';
  difficulty: 'easy' | 'medium' | 'hard';
  questions_count: number;
  time_limit_minutes: number;
}

const AITestGenerator: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Проверка прав администратора
  if (user?.role !== 'admin') {
    return (
      <Layout>
        <Alert
          message={t('aiGenerator.accessDenied')}
          description={t('aiGenerator.adminOnly')}
          type="error"
          showIcon
        />
      </Layout>
    );
  }

  const onFinish = async (values: AITestGenerateRequest) => {
    setLoading(true);
    try {
      await api.post('/tests/generate-ai', values);
      message.success(t('aiGenerator.success'));
      navigate(`/admin/tests`);
    } catch (error: any) {
      console.error('Ошибка генерации теста:', error);
      message.error(getApiErrorMessage(error, t('aiGenerator.error')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>{t('aiGenerator.title')}</span>
          </Space>
        }
        style={{ maxWidth: 800, margin: '0 auto' }}
      >
        <Paragraph type="secondary" style={{ marginBottom: 24 }}>
          {t('aiGenerator.description')}
        </Paragraph>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            block: 'products',
            difficulty: 'medium',
            questions_count: 20,
            time_limit_minutes: 30,
          }}
        >
          <Form.Item
            name="title"
            label={t('aiGenerator.form.title')}
            rules={[{ required: true, message: t('aiGenerator.form.titleRequired') }]}
          >
            <Input placeholder={t('aiGenerator.form.titlePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('aiGenerator.form.description')}
          >
            <TextArea
              rows={3}
              placeholder={t('aiGenerator.form.descriptionPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="block"
            label={t('aiGenerator.form.block')}
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="products">
                {t('tests.block.products')}
              </Select.Option>
              <Select.Option value="vehicles">
                {t('tests.block.vehicles')}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="difficulty"
            label={t('aiGenerator.form.difficulty')}
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="easy">
                {t('aiGenerator.difficulty.easy')} - {t('aiGenerator.difficulty.easyDesc')}
              </Select.Option>
              <Select.Option value="medium">
                {t('aiGenerator.difficulty.medium')} - {t('aiGenerator.difficulty.mediumDesc')}
              </Select.Option>
              <Select.Option value="hard">
                {t('aiGenerator.difficulty.hard')} - {t('aiGenerator.difficulty.hardDesc')}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="questions_count"
            label={t('aiGenerator.form.questionsCount')}
            rules={[
              { required: true },
              { type: 'number', min: 1, max: 100 },
            ]}
          >
            <InputNumber
              min={1}
              max={100}
              style={{ width: '100%' }}
              placeholder={t('aiGenerator.form.questionsCountPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="time_limit_minutes"
            label={t('aiGenerator.form.timeLimit')}
            rules={[
              { required: true },
              { type: 'number', min: 5, max: 180 },
            ]}
          >
            <InputNumber
              min={5}
              max={180}
              style={{ width: '100%' }}
              placeholder={t('aiGenerator.form.timeLimitPlaceholder')}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<ThunderboltOutlined />}
                loading={loading}
                size="large"
              >
                {t('aiGenerator.generate')}
              </Button>
              <Button onClick={() => navigate('/admin/tests')}>
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {loading && (
          <Alert
            message={t('aiGenerator.generating')}
            description={t('aiGenerator.generatingDesc')}
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>
    </Layout>
  );
};

export default AITestGenerator;
